import * as React from 'react';
import { Field } from 'formik';
import { addYears, format } from 'date-fns';
import { useParams } from 'react-router-dom';
import { useTheme, useMediaQuery, Grid, Skeleton, IconButton } from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons';

import { useDealQuery } from '../hooks';
import { NotNull, NumberUtils } from '../utils';
import {
	AnalysisMetricTypes,
	DealFinancialMetric,
	DealFinancialMetricType,
	DealFinancialMetricTypeMap,
} from '../models';
import { CerebroHeading, CerebroIcon, CerebroLabel, CerebroMaskedInput, CerebroText } from '.';
import { MaxCurrencyLength } from '../utils/platform';

export const IncomeStatementRow: React.FC<IncomeStatementRowProps> = ({
	isLoading,
	isSubmitting,

	metricType,
	formValues,

	onInfoClick,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const { guid: dealGuid } = useParams<'guid'>();

	const dealQuery = useDealQuery(dealGuid);

	if (metricType === undefined) return null;

	const dealCreatedOn = dealQuery.data?.financialStartDate;

	const twoYearsAgoKey = getFormKey(metricType, 'twoYearsAgo');
	const previousYearKey = getFormKey(metricType, 'previousFullYear');
	const ttmKey = getFormKey(metricType, 'trailingTwelveMonths');
	const projectedFullYearKey = getFormKey(metricType, 'projectedFullYear');
	const futureYearKey = getFormKey(metricType, 'futureYear');

	const isAdjustedEbitda = metricType === 'AdjustedEbitda';
	const isMrr = metricType === 'Mrr';

	return (
		<>
			<Grid item xs={12} md={2} sx={{ display: 'flex' }} alignItems="center">
				<CerebroHeading size="h3" gutterBottom sx={{ marginTop: { xs: 1, md: 0 } }}>
					{DealFinancialMetricTypeMap[metricType]}
				</CerebroHeading>
				{onInfoClick && (
					<CerebroIcon>
						<IconButton onClick={onInfoClick}>
							<FontAwesomeIcon icon={faInfoCircle} />
						</IconButton>
					</CerebroIcon>
				)}
			</Grid>

			{!isMrr && (
				<Grid item xs={12} md={2}>
					{isMobile && (
						<CerebroLabel htmlFor={previousYearKey} gutterBottom>
							{dealQuery.isLoading && <Skeleton height={50} />}
							{dealQuery.isSuccess && getYearLabel('twoYearsAgo', dealCreatedOn)}
						</CerebroLabel>
					)}

					{isLoading && <Skeleton height={75} />}

					{isAdjustedEbitda && !isLoading && (
						<CerebroText align={isMobile ? 'left' : 'center'} weight="medium">
							{NumberUtils.FormatNumber(
								adjustEbitda(formValues, 'twoYearsAgo'),
								'currency',
								NumberUtils.NoDecimalsFormatOptions
							)}
						</CerebroText>
					)}

					{!isAdjustedEbitda && !isLoading && (
						<Field
							formatas="currency"
							id={twoYearsAgoKey}
							placeholder="$0"
							calculator="true"
							maxLength={MaxCurrencyLength}
							disabled={isSubmitting}
							as={CerebroMaskedInput}
							name={twoYearsAgoKey}
						/>
					)}
				</Grid>
			)}

			{!isMrr && (
				<Grid item xs={12} md={2}>
					{isMobile && (
						<CerebroLabel htmlFor={previousYearKey} gutterBottom>
							{dealQuery.isLoading && <Skeleton height={50} />}
							{dealQuery.isSuccess && getYearLabel('previousFullYear', dealCreatedOn)}
						</CerebroLabel>
					)}

					{isLoading && <Skeleton height={75} />}

					{isAdjustedEbitda && !isLoading && (
						<CerebroText align={isMobile ? 'left' : 'center'} weight="medium">
							{NumberUtils.FormatNumber(
								adjustEbitda(formValues, 'previousFullYear'),
								'currency',
								NumberUtils.NoDecimalsFormatOptions
							)}
						</CerebroText>
					)}

					{!isAdjustedEbitda && !isLoading && (
						<Field
							formatas="currency"
							id={previousYearKey}
							placeholder="$0"
							calculator="true"
							maxLength={MaxCurrencyLength}
							disabled={isSubmitting}
							as={CerebroMaskedInput}
							name={previousYearKey}
						/>
					)}
				</Grid>
			)}

			<Grid item xs={12} md={2}>
				{isMobile && (
					<CerebroLabel htmlFor={ttmKey} gutterBottom>
						{dealQuery.isLoading && <Skeleton height={50} />}
						{dealQuery.isSuccess && getYearLabel('trailingTwelveMonths', dealCreatedOn)}
					</CerebroLabel>
				)}

				{isLoading && <Skeleton height={75} />}

				{isAdjustedEbitda && !isLoading && (
					<CerebroText align={isMobile ? 'left' : 'center'} weight="medium">
						{NumberUtils.FormatNumber(
							adjustEbitda(formValues, 'trailingTwelveMonths'),
							'currency',
							NumberUtils.NoDecimalsFormatOptions
						)}
					</CerebroText>
				)}
				{!isAdjustedEbitda && !isLoading && (
					<Field
						formatas="currency"
						id={ttmKey}
						calculator="true"
						placeholder="$0"
						maxLength={MaxCurrencyLength}
						as={CerebroMaskedInput}
						name={ttmKey}
						disabled={isSubmitting}
					/>
				)}
			</Grid>

			{isMrr && (
				<Grid item xs={12} md={6}>
					<CerebroText variant="hint">
						Enter the Monthly Recurring Revenue value for the most recent period.
					</CerebroText>
				</Grid>
			)}

			{!isMrr && (
				<Grid item xs={12} md={2}>
					{isMobile && (
						<CerebroLabel htmlFor={projectedFullYearKey} gutterBottom>
							{dealQuery.isLoading && <Skeleton height={50} />}
							{dealQuery.isSuccess && getYearLabel('projectedFullYear', dealCreatedOn)}
						</CerebroLabel>
					)}

					{isLoading && <Skeleton height={75} />}

					{isAdjustedEbitda && !isLoading && (
						<CerebroText align={isMobile ? 'left' : 'center'} weight="medium">
							{NumberUtils.FormatNumber(
								adjustEbitda(formValues, 'projectedFullYear'),
								'currency',
								NumberUtils.NoDecimalsFormatOptions
							)}
						</CerebroText>
					)}

					{!isAdjustedEbitda && !isLoading && (
						<Field
							formatas="currency"
							disabled={isSubmitting}
							calculator="true"
							maxLength={MaxCurrencyLength}
							as={CerebroMaskedInput}
							id={projectedFullYearKey}
							placeholder="$0"
							name={projectedFullYearKey}
						/>
					)}
				</Grid>
			)}

			{!isMrr && (
				<Grid item xs={12} md={2}>
					{isMobile && (
						<CerebroLabel htmlFor={futureYearKey} gutterBottom>
							{dealQuery.isLoading && <Skeleton height={50} />}
							{dealQuery.isSuccess && getYearLabel('futureYear', dealCreatedOn)}
						</CerebroLabel>
					)}

					{isLoading && <Skeleton height={75} />}

					{isAdjustedEbitda && !isLoading && (
						<CerebroText align={isMobile ? 'left' : 'center'} weight="medium">
							{NumberUtils.FormatNumber(
								adjustEbitda(formValues, 'futureYear'),
								'currency',
								NumberUtils.NoDecimalsFormatOptions
							)}
						</CerebroText>
					)}

					{!isAdjustedEbitda && !isLoading && (
						<Field
							formatas="currency"
							disabled={isSubmitting}
							calculator="true"
							maxLength={MaxCurrencyLength}
							as={CerebroMaskedInput}
							id={futureYearKey}
							placeholder="$0"
							name={futureYearKey}
						/>
					)}
				</Grid>
			)}
		</>
	);
};

export type IncomeStatementFormValues = {
	[key in MetricYears as `${DealFinancialMetricType}|${key}`]: string;
};

type IncomeStatementRowProps = {
	isLoading: boolean;
	isSubmitting: boolean;
	formValues: IncomeStatementFormValues;
	metricType: DealFinancialMetricType | undefined;

	onInfoClick?: () => void;
};

type MetricYears = 'twoYearsAgo' | 'previousFullYear' | 'trailingTwelveMonths' | 'projectedFullYear' | 'futureYear';

const getFormKey = (metricType: DealFinancialMetricType, key: MetricYears): keyof IncomeStatementFormValues =>
	`${metricType}|${key}`;

export const adjustEbitda = (formValues: IncomeStatementFormValues, year: MetricYears) => {
	return (
		NumberUtils.ParseDecimal(formValues[getFormKey('Ebitda', year)], 0) +
		NumberUtils.ParseDecimal(formValues[getFormKey('Adjustments', year)], 0)
	);
};

export function getYearLabel(year: MetricYears, dealCreationDate?: string) {
	if (dealCreationDate === undefined) return;

	const effectiveDate = new Date(dealCreationDate);
	const dateYear = effectiveDate.getUTCFullYear();

	switch (year) {
		case 'twoYearsAgo':
			return 'Prior Year' + ` ${dateYear - 1}`;

		case 'previousFullYear':
			return `${dateYear} YTD`;

		case 'trailingTwelveMonths':
			return 'TTM (Trailing 12 Months)';

		case 'projectedFullYear':
			return `${dateYear} Projected Full Year`;

		case 'futureYear':
			return `${dateYear + 1} Projected Full Year`;

		default:
			return 'N/A';
	}
}

export function buildFormValues(metrics: DealFinancialMetric[] | undefined): IncomeStatementFormValues | null {
	if (metrics == null) return null;

	return Object.fromEntries(
		metrics.flatMap(
			({
				financialMetricType,
				guid,
				twoYearsAgo,
				previousFullYear,
				trailingTwelveMonths,
				projectedFullYear,
				futureYear,
			}) => {
				if (financialMetricType == null) return [];

				const pairs: [keyof IncomeStatementFormValues, string][] = [
					[
						getFormKey(financialMetricType, 'twoYearsAgo'),
						NumberUtils.FormatNumber(twoYearsAgo, 'currency', NumberUtils.NoDecimalsFormatOptions) ?? '',
					],
					[
						getFormKey(financialMetricType, 'previousFullYear'),
						NumberUtils.FormatNumber(previousFullYear, 'currency', NumberUtils.NoDecimalsFormatOptions) ??
							'',
					],
					[
						getFormKey(financialMetricType, 'trailingTwelveMonths'),
						NumberUtils.FormatNumber(
							trailingTwelveMonths,
							'currency',
							NumberUtils.NoDecimalsFormatOptions
						) ?? '',
					],
					[
						getFormKey(financialMetricType, 'projectedFullYear'),
						NumberUtils.FormatNumber(projectedFullYear, 'currency', NumberUtils.NoDecimalsFormatOptions) ??
							'',
					],
					[
						getFormKey(financialMetricType, 'futureYear'),
						NumberUtils.FormatNumber(futureYear, 'currency', NumberUtils.NoDecimalsFormatOptions) ?? '',
					],
				];

				return pairs;
			}
		)
	) as IncomeStatementFormValues;
}

export const DefaultIncomeStateFormValues = {
	'TotalRevenue|twoYearsAgo': '',
	'TotalRevenue|previousFullYear': '',
	'TotalRevenue|trailingTwelveMonths': '',
	'TotalRevenue|projectedFullYear': '',
	'TotalRevenue|futureYear': '',
	'NetIncome|twoYearsAgo': '',
	'NetIncome|previousFullYear': '',
	'NetIncome|trailingTwelveMonths': '',
	'NetIncome|projectedFullYear': '',
	'NetIncome|futureYear': '',
	'Ebitda|twoYearsAgo': '',
	'Ebitda|previousFullYear': '',
	'Ebitda|trailingTwelveMonths': '',
	'Ebitda|projectedFullYear': '',
	'Ebitda|futureYear': '',
	'Adjustments|twoYearsAgo': '',
	'Adjustments|previousFullYear': '',
	'Adjustments|trailingTwelveMonths': '',
	'Adjustments|projectedFullYear': '',
	'Adjustments|futureYear': '',
	'AdjustedEbitda|twoYearsAgo': '',
	'AdjustedEbitda|previousFullYear': '',
	'AdjustedEbitda|trailingTwelveMonths': '',
	'AdjustedEbitda|projectedFullYear': '',
	'AdjustedEbitda|futureYear': '',
	'Mrr|trailingTwelveMonths': '',
} as IncomeStatementFormValues;

export function parseFormValuesToPatches(
	analysisMetricType: AnalysisMetricTypes,
	metrics: DealFinancialMetric[] | undefined,
	formValues: IncomeStatementFormValues
): Partial<DealFinancialMetric>[] {
	if (metrics === undefined) return [];

	return metrics
		.map(({ guid, financialMetricType }) => {
			if (financialMetricType === undefined) return null;

			const isAdjustedEbitda = financialMetricType === 'AdjustedEbitda';

			const patch: Partial<DealFinancialMetric> = {
				guid: guid,
				analysisMetricType,
				financialMetricType,

				twoYearsAgo: isAdjustedEbitda
					? adjustEbitda(formValues, 'twoYearsAgo')
					: NumberUtils.ParseDecimal(formValues[getFormKey(financialMetricType, 'twoYearsAgo')]),

				previousFullYear: isAdjustedEbitda
					? adjustEbitda(formValues, 'previousFullYear')
					: NumberUtils.ParseDecimal(formValues[getFormKey(financialMetricType, 'previousFullYear')]),

				trailingTwelveMonths: isAdjustedEbitda
					? adjustEbitda(formValues, 'trailingTwelveMonths')
					: NumberUtils.ParseDecimal(formValues[getFormKey(financialMetricType, 'trailingTwelveMonths')]),

				projectedFullYear: isAdjustedEbitda
					? adjustEbitda(formValues, 'projectedFullYear')
					: NumberUtils.ParseDecimal(formValues[getFormKey(financialMetricType, 'projectedFullYear')]),

				futureYear: isAdjustedEbitda
					? adjustEbitda(formValues, 'futureYear')
					: NumberUtils.ParseDecimal(formValues[getFormKey(financialMetricType, 'futureYear')]),
			};

			return patch;
		})
		.filter(NotNull);
}

export const IncomeStatementMetricTypes: DealFinancialMetricType[] = [
	'TotalRevenue',
	'NetIncome',
	'Ebitda',
	'Adjustments',
	'AdjustedEbitda',
	'Mrr',
];
